import { createContext, useContext, useEffect, useMemo } from 'react';
import { useBook } from './BookContext';

const ThemeContext = createContext();

// Theme presets: dark (The King's Return) and light (The Voice of the Spirit)
const THEMES = {
  dark: {
    name: 'dark',
    label: 'Dark Parchment',
    // Backgrounds
    bgPrimary: '#060503',
    bgSecondary: '#0a0703',
    bgCard: 'rgba(10,7,3,0.8)',
    bgCardHover: 'rgba(10,7,3,0.9)',
    bgOverlay: 'rgba(10,7,3,0.95)',
    bgNavbar: 'rgba(6,5,3,0.5)',
    bgInput: 'rgba(10,7,3,0.6)',
    // Text
    textPrimary: 'rgba(220,190,130,0.92)',
    textSecondary: 'rgba(220,190,130,0.80)',
    textMuted: 'rgba(220,190,130,0.68)',
    textHeading: '#d4a54a',
    // Accents
    accentPrimary: '#d4a54a',
    accentSecondary: '#92400e',
    accentGradient: 'linear-gradient(180deg, #fcd34d 0%, #d97706 50%, #92400e 100%)',
    accentBg: 'rgba(212,165,74,0.08)',
    accentBorder: 'rgba(212,165,74,0.25)',
    // Labels
    labelColor: '#dc2626',
    successColor: '#10b981',
    // Borders
    borderPrimary: 'rgba(120,80,20,0.15)',
    borderSecondary: 'rgba(120,80,20,0.1)',
    // Scripture
    scriptureText: '#5b8ab5',
    scriptureBg: 'rgba(30,58,95,0.08)',
    scriptureBorder: 'rgba(30,58,95,0.25)',
    // Misc
    particleColor: 'rgba(245,158,11,0.8)',
    navText: '#b87a1a',
    buyBtnBg: 'linear-gradient(135deg, #b45309, #d97706)',
  },
  light: {
    name: 'light',
    label: 'Light Scroll',
    // Backgrounds
    bgPrimary: '#f8f5ee',
    bgSecondary: '#f0ebe0',
    bgCard: 'rgba(255,255,255,0.85)',
    bgCardHover: 'rgba(255,255,255,0.95)',
    bgOverlay: 'rgba(248,245,238,0.97)',
    bgNavbar: 'rgba(248,245,238,0.85)',
    bgInput: 'rgba(255,255,255,0.7)',
    // Text
    textPrimary: '#3b2f1e',
    textSecondary: '#6b5d4d',
    textMuted: '#9b8d7d',
    textHeading: '#2c4a1e',
    // Accents
    accentPrimary: '#2c4a1e',
    accentSecondary: '#4a7c3f',
    accentGradient: 'linear-gradient(180deg, #3d6b2e 0%, #2c4a1e 50%, #1a3012 100%)',
    accentBg: 'rgba(44,74,30,0.06)',
    accentBorder: 'rgba(44,74,30,0.2)',
    // Labels
    labelColor: '#8b4513',
    successColor: '#2d7a4f',
    // Borders
    borderPrimary: 'rgba(139,109,73,0.2)',
    borderSecondary: 'rgba(139,109,73,0.1)',
    // Scripture
    scriptureText: '#1a3a5c',
    scriptureBg: 'rgba(26,58,92,0.04)',
    scriptureBorder: 'rgba(26,58,92,0.12)',
    // Misc
    particleColor: 'rgba(44,74,30,0.4)',
    navText: '#6b5d4d',
    buyBtnBg: 'linear-gradient(135deg, #2c4a1e, #4a7c3f)',
  },
};

export function ThemeProvider({ children }) {
  const { selectedBook } = useBook();

  const theme = useMemo(() => {
    const themeKey = selectedBook?.theme || 'dark';
    return THEMES[themeKey] || THEMES.dark;
  }, [selectedBook?.theme]);

  // Apply CSS custom properties to document root
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(theme).forEach(([key, value]) => {
      if (typeof value === 'string') {
        root.style.setProperty(`--theme-${camelToKebab(key)}`, value);
      }
    });
    // Also set a data attribute for CSS selectors
    root.setAttribute('data-theme', theme.name);
    return () => {
      root.removeAttribute('data-theme');
    };
  }, [theme]);

  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    // Fallback to dark theme if used outside provider
    return THEMES.dark;
  }
  return context;
}

export { THEMES };

// Helper to convert camelCase to kebab-case
function camelToKebab(str) {
  return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}
